import React from 'react';

import { observer } from 'mobx-react';
import { reverse } from 'named-urls';
import { Link } from 'react-router-dom';

import EvidenceFiles from 'components/EvidenceFiles/EvidenceFiles';
import { savedEvidenceFiles } from 'components/EvidenceFiles/utils';
import UserRow from 'components/UserRow';
import {
  DueByValue,
  FieldContainer,
  FieldLabel,
  FieldValue,
} from 'containers/Tasks/components/ActionsSchedulesTable/ScheduleDrawer/styles';
import { EntityModules } from 'core/constants';
import routes from 'core/routes';
import { RiskActionDetail } from 'utils/api/modules/risks';
import { ScheduleStatus } from 'utils/api/modules/tasks/tasks.constants';
import { formatDueDate } from 'utils/date';
import { formatLibraryId } from 'utils/formatters/libraryId';

import RiskActionStatusChip from '../RiskActionStatusChip';
import { dueDateStatus } from '../columns';
import { StyledEvidenceAnswer, StyledFieldStack, StyledRiskLinks } from './styles';

const RisksValue: React.FunctionComponent<{ action: RiskActionDetail }> = ({ action }) => {
  const { risks, risksCount, control } = action;
  if (risksCount === 0) return <FieldValue>-</FieldValue>;
  const more = risksCount - risks.length;
  const related = `${reverse(routes.dashboard.risks.controlDetails, { entityId: control.uuid })}?tab=related-tab`;
  return (
    <StyledRiskLinks>
      {risks.map((risk) => (
        <Link key={risk.uuid} to={reverse(routes.dashboard.risks.details, { entityId: risk.uuid })}>
          {risk.name}
        </Link>
      ))}
      {more > 0 && <Link to={related}>+{more}</Link>}
    </StyledRiskLinks>
  );
};

const ControlActionFields: React.FunctionComponent<{ action: RiskActionDetail }> = ({ action }) => {
  const { control } = action;
  const controlLabel = [formatLibraryId(EntityModules.Controls, control.number), control.name]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <FieldContainer>
        <FieldLabel>Status</FieldLabel>
        <RiskActionStatusChip status={action.status} />
      </FieldContainer>
      <FieldContainer>
        <FieldLabel>Control</FieldLabel>
        <FieldValue>
          <Link
            to={reverse(routes.dashboard.risks.controlDetails, { entityId: control.uuid })}
            data-testid="control-action-control-link"
          >
            {controlLabel}
          </Link>
        </FieldValue>
      </FieldContainer>
      {control.description && (
        <FieldContainer>
          <FieldLabel>Description</FieldLabel>
          <FieldValue>{control.description}</FieldValue>
        </FieldContainer>
      )}
      <FieldContainer>
        <FieldLabel>{action.risksCount > 1 ? 'Risks' : 'Risk'}</FieldLabel>
        <RisksValue action={action} />
      </FieldContainer>
      <FieldContainer>
        <FieldLabel>Due By</FieldLabel>
        <DueByValue $overdue={dueDateStatus(action) === ScheduleStatus.Overdue}>
          {action.dueDate && formatDueDate(action.dueDate, { showTime: true }).name}
        </DueByValue>
      </FieldContainer>
      <FieldContainer>
        <FieldLabel>Assigned To</FieldLabel>
        <UserRow user={action.assignedTo} />
      </FieldContainer>
      <FieldContainer data-testid="control-action-requested-by">
        <FieldLabel>Requested By</FieldLabel>
        <UserRow user={action.requestedBy} />
      </FieldContainer>
      {action.evidence && (
        <StyledFieldStack>
          <FieldLabel>Evidence</FieldLabel>
          <StyledEvidenceAnswer data-testid="control-action-evidence-text">{action.evidence.text}</StyledEvidenceAnswer>
          <EvidenceFiles files={savedEvidenceFiles(action.evidence.attachments)} />
        </StyledFieldStack>
      )}
    </>
  );
};

export default observer(ControlActionFields);
